import clsx from 'clsx';
import Cookies from 'js-cookie';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { InView } from 'react-intersection-observer';

import { TextButton } from '@components/Button/variants';
import IconClose from '@components/Images/Rebranding/IconClose';
import { useEventTracking } from '@hooks/index';
import { discounted_upgrade_path } from '@routes';
import {
  ClickTarget,
  ContentType,
  ImpressionElement,
  TrackingLocation,
  click,
  impression,
} from '@tracking';

export const AUDIO_LISTEN_ONBOARDING_COOKIE = 'audio-listen-onboarding-dismissed';
export const TOOLTIP_BUTTON_TEXT = 'Upgrade today';
export const TOOLTIP_BUTTON_TEXT_NON_MONTHLY = 'Got it';
export const OFFER_CODE = 'sub-upg-mon-ann-299-t2a-articles';

type ListenOnboardingPanelProps = {
  isMonthlySubscriber: boolean;
  onDismiss: () => void;
  contentType: ContentType;
};

const RenderTooltipButton = ({
  isMonthlySubscriber,
  onDismiss,
  trackEvent,
}: {
  isMonthlySubscriber: boolean;
  onDismiss: () => void;
  trackEvent: ReturnType<typeof useEventTracking>['trackEvent'];
}) => {
  if (isMonthlySubscriber) {
    return (
      <TextButton
        as="link"
        href={discounted_upgrade_path({ offer: OFFER_CODE })}
        className="inline !w-fit p-0 font-suisse-intl text-button-md text-primary-pink underline"
        onClickCapture={() =>
          trackEvent(click({ clickTarget: ClickTarget.Upgrade, clickText: TOOLTIP_BUTTON_TEXT }))
        }
      >
        {TOOLTIP_BUTTON_TEXT}
      </TextButton>
    );
  } else {
    return (
      <TextButton
        as="button"
        className="inline !w-fit p-0 font-suisse-intl text-button-md text-primary-pink underline"
        onClick={onDismiss}
        onClickCapture={() =>
          trackEvent(
            click({ clickTarget: ClickTarget.Close, clickText: TOOLTIP_BUTTON_TEXT_NON_MONTHLY })
          )
        }
      >
        {TOOLTIP_BUTTON_TEXT_NON_MONTHLY}
      </TextButton>
    );
  }
};

const ListenOnboardingPanel = ({
  isMonthlySubscriber,
  onDismiss,
  contentType,
}: ListenOnboardingPanelProps) => {
  const { trackEvent } = useEventTracking({
    location: TrackingLocation.AudioListenOnboardingTooltip,
  });
  const contentTypeText = contentType === ContentType.Article ? 'article' : 'briefing';
  const toolTipText = isMonthlySubscriber
    ? `Audio ${contentTypeText}s are exclusively available to Annual and Pro subscribers.`
    : `Prefer listening? You can now listen to ${contentTypeText}s.`;

  return (
    <InView
      triggerOnce={true}
      onChange={(inView) => {
        inView &&
          trackEvent(
            impression({
              impressionElement: isMonthlySubscriber
                ? ImpressionElement.TextToAudioMonthlyTooltip
                : ImpressionElement.TextToAudioOnboardingTooltip,
            })
          );
      }}
    >
      <div
        role="dialog"
        aria-labelledby="audio-listen-onboarding-title"
        aria-modal="false"
        className={clsx(
          'shadow-lg pointer-events-auto absolute bottom-full left-[30%] z-20 mb-12 w-[min(100vw-48px,300px)] -translate-x-1/2 rounded-4 bg-grayscale-charcoal px-20 pb-16 pt-20 text-white print:hidden sm:left-1/2 sm:w-[min(100vw-48px,400px)]',
          "after:absolute after:-bottom-8 after:left-1/2 after:h-16 after:w-16 after:-translate-x-1/2 after:rotate-45 after:rounded-br-4 after:bg-grayscale-charcoal after:content-['']"
        )}
      >
        <button
          type="button"
          className="absolute right-8 top-8 flex h-32 w-32 touch-manipulation items-center justify-center border-none bg-transparent text-white"
          onClick={onDismiss}
          onClickCapture={() => trackEvent(click({ clickTarget: ClickTarget.Close }))}
          aria-label="Close audio tip"
        >
          <IconClose className="stroke-2" height={12} width={12} />
        </button>
        <p
          id="audio-listen-onboarding-title"
          className="m-0 pr-28 font-suisse-intl text-body-md font-normal leading-snug text-white"
        >
          {toolTipText}
        </p>
        <div className="mt-8">
          <RenderTooltipButton
            isMonthlySubscriber={isMonthlySubscriber}
            onDismiss={onDismiss}
            trackEvent={trackEvent}
          />
        </div>
      </div>
    </InView>
  );
};

export type AudioListenOnboardingTriggerProps = {
  children: React.ReactElement<{ onClick?: React.MouseEventHandler }>;
  isSubscriber: boolean;
  isMonthlySubscriber: boolean;
  className?: string;
  onListenOnboardingOpenChange?: (open: boolean) => void;
  contentType: ContentType;
};

const ToolTip = ({
  children,
  isSubscriber,
  isMonthlySubscriber,
  className,
  onListenOnboardingOpenChange,
  contentType,
}: AudioListenOnboardingTriggerProps) => {
  const containerRef = useRef<HTMLSpanElement>(null);

  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!isSubscriber) {
      setVisible(false);
      return;
    }
    if (isMonthlySubscriber) return;
    if (!Cookies.get(AUDIO_LISTEN_ONBOARDING_COOKIE)) {
      setVisible(true);
    }
  }, [isMonthlySubscriber, isSubscriber]);

  useEffect(() => {
    onListenOnboardingOpenChange?.(visible);
  }, [visible, onListenOnboardingOpenChange]);

  const dismiss = useCallback(() => {
    if (!isMonthlySubscriber) {
      const expires = new Date();
      expires.setMonth(expires.getMonth() + 6);
      Cookies.set(AUDIO_LISTEN_ONBOARDING_COOKIE, 'true', { expires });
    }
    setVisible(false);
  }, [isMonthlySubscriber]);

  useEffect(() => {
    if (!visible) return;
    const onPointerDown = (event: PointerEvent) => {
      const node = containerRef.current;
      if (node && !node.contains(event.target as Node)) {
        dismiss();
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [visible, dismiss]);

  const handleListenClick: React.MouseEventHandler = (event) => {
    if (isSubscriber && isMonthlySubscriber) {
      setVisible(true);
    }
    children.props.onClick?.(event);
  };

  return (
    <span ref={containerRef} className={className}>
      {visible && (
        <ListenOnboardingPanel
          isMonthlySubscriber={isMonthlySubscriber}
          onDismiss={dismiss}
          contentType={contentType}
        />
      )}
      {React.cloneElement(children, { onClick: handleListenClick })}
    </span>
  );
};

export default ToolTip;
